import React, { useState } from 'react';
import { Settings, User, CreditCard, Edit, Mail } from 'lucide-react';
import ClientProfileEdit from './ClientProfileEdit';
import MandateManagement from './MandateManagement';
import ClientRecipientsManagement from './ClientRecipientsManagement';

const ClientSettings = ({ profile, onProfileUpdate, permissions }) => {
  const [activeTab, setActiveTab] = useState('profile');
  const [editingProfile, setEditingProfile] = useState(false);

  const handleProfileSave = async () => {
    await onProfileUpdate();
    setEditingProfile(false);
  };

  const handleProfileCancel = () => {
    setEditingProfile(false);
  };

  return (
    <div className="grid md:grid-cols-4 gap-6">
      <div className="md:col-span-1">
        <div className="bg-white rounded-lg border border-gray-200 shadow">
          <div className="px-4 py-3 border-b border-gray-200">
            <h6 className="text-base font-semibold text-gray-900 mb-0 flex items-center">
              <Settings size={16} className="mr-2" />
              Settings
            </h6>
          </div>
          <div className="p-0">
            <div className="divide-y divide-gray-200">
              <button
                className={`w-full text-left px-4 py-3 flex items-center hover:bg-gray-50 transition duration-200 ${
                  activeTab === 'profile' 
                    ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-700' 
                    : 'text-gray-700'
                }`}
                onClick={() => setActiveTab('profile')}
              >
                <User size={16} className="mr-2" />
                Profile
              </button>
              <button
                className={`w-full text-left px-4 py-3 flex items-center hover:bg-gray-50 transition duration-200 ${
                  activeTab === 'recipients' 
                    ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-700' 
                    : 'text-gray-700'
                }`}
                onClick={() => setActiveTab('recipients')}
              >
                <Mail size={16} className="mr-2" />
                Email Recipients
              </button>
              {permissions?.addPaymentMethods && (
                <button
                  className={`w-full text-left px-4 py-3 flex items-center hover:bg-gray-50 transition duration-200 ${
                    activeTab === 'payments' 
                      ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-700' 
                      : 'text-gray-700'
                  }`}
                  onClick={() => setActiveTab('payments')}
                >
                  <CreditCard size={16} className="mr-2" />
                  Payment Methods
                </button>
              )}
              {/* <button
                className={`w-full text-left px-4 py-3 flex items-center hover:bg-gray-50 transition duration-200 ${
                  activeTab === 'security' 
                    ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-700' 
                    : 'text-gray-700'
                }`}
                onClick={() => setActiveTab('security')}
              >
                <Shield size={16} className="mr-2" />
                Security
              </button> */}
            </div>
          </div>
        </div>
      </div>

      <div className="md:col-span-3">
        {activeTab === 'profile' && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h5 className="text-xl font-semibold text-gray-900 mb-0">Profile Settings</h5>
              {!editingProfile && (
                <button
                  className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition duration-200 flex items-center"
                  onClick={() => setEditingProfile(true)}
                >
                  <Edit size={16} className="mr-1" />
                  Edit Profile
                </button>
              )}
            </div>

            {editingProfile ? (
              <ClientProfileEdit
                profile={profile}
                onSave={handleProfileSave}
                onCancel={handleProfileCancel}
              />
            ) : (
              profile && (
                <div className="bg-white rounded-lg border border-gray-200 shadow">
                  <div className="p-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <div className="mb-4">
                          <label className="block text-sm font-bold text-gray-900 mb-1">Company Name</label>
                          <p className="text-gray-700">{profile.name}</p>
                        </div>
                        <div className="mb-4">
                          <label className="block text-sm font-bold text-gray-900 mb-1">Email Address</label>
                          <p className="text-gray-700">{profile.email}</p>
                        </div>
                        <div className="mb-4">
                          <label className="block text-sm font-bold text-gray-900 mb-1">Phone Number</label>
                          <p className="text-gray-700">{profile.phone || 'Not provided'}</p>
                        </div>
                      </div>
                      <div>
                        <div className="mb-4">
                          <label className="block text-sm font-bold text-gray-900 mb-1">Contact Person</label>
                          <p className="text-gray-700">{profile.contact_person || 'Not provided'}</p>
                        </div>
                        <div className="mb-4">
                          <label className="block text-sm font-bold text-gray-900 mb-1">Website</label>
                          <p className="text-gray-700">
                            {profile.website ? (
                              <a 
                                href={profile.website} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-800"
                              >
                                {profile.website}
                              </a>
                            ) : (
                              'Not provided'
                            )}
                          </p>
                        </div>
                        <div className="mb-4">
                          <label className="block text-sm font-bold text-gray-900 mb-1">Address</label>
                          <p className="text-gray-700">{profile.address || 'Not provided'}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )
            )}
          </div>
        )}

        {activeTab === 'recipients' && (
          <div>
            <h5 className="text-xl font-semibold text-gray-900 mb-4">Email Recipients</h5>
            <p className="text-gray-500 mb-6">
              Manage who receives your invoices, quotes, and other important documents via email.
            </p>
            <ClientRecipientsManagement />
          </div>
        )}

        {activeTab === 'payments' && permissions?.addPaymentMethods && (
          <div>
            <h5 className="text-xl font-semibold text-gray-900 mb-4">Payment Methods</h5>
            <div className="bg-white rounded-lg border border-gray-200 shadow">
              <div className="p-6">
                <p className="text-gray-500 mb-4">
                  Manage your payment methods and direct debit mandates for automatic payments.
                </p>
                <MandateManagement />
              </div>
            </div>
          </div>
        )}

        {activeTab === 'security' && (
          <div>
            <h5 className="text-xl font-semibold text-gray-900 mb-4">Security Settings</h5>
            <div className="bg-white rounded-lg border border-gray-200 shadow">
              <div className="p-6">
                <div className="mb-6">
                  <h6 className="text-base font-semibold text-gray-900 mb-2">Password</h6>
                  <p className="text-gray-500 mb-3">Update your password to keep your account secure.</p>
                  <button className="border border-blue-600 text-blue-600 hover:bg-blue-50 font-medium py-2 px-4 rounded-md transition duration-200">
                    Change Password
                  </button>
                </div>
                
                <div className="mb-6">
                  <h6 className="text-base font-semibold text-gray-900 mb-2">Session Management</h6>
                  <p className="text-gray-500 mb-3">Manage your active sessions and logout from all devices.</p>
                  <button className="border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium py-2 px-4 rounded-md transition duration-200">
                    View Active Sessions
                  </button>
                </div>

                <div className="mb-6">
                  <h6 className="text-base font-semibold text-gray-900 mb-2">Account Security</h6>
                  <p className="text-gray-500 mb-3">Review recent account activity and security settings.</p>
                  <button className="border border-cyan-600 text-cyan-600 hover:bg-cyan-50 font-medium py-2 px-4 rounded-md transition duration-200">
                    View Security Log
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientSettings;